import React, { lazy } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';

// layouts
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
import { checkPermission } from './utils/Permissions';
import PrivateRoute from './privateRoute';


// Lazy loading is used to optimize performance.
const LogHistory = lazy(() => import('./pages/log-history/List'));
const ViewLog = lazy(() => import('./pages/log-history/View'));
const DashboardAppPage = lazy(() => import('./pages/DashboardAppPage'));
const UserPage = lazy(() => import('./pages/users/List'));
const CreateUser = lazy(() => import('./pages/users/Create'));
const EditUser = lazy(() => import('./pages/users/Edit'));
const RoleList = lazy(() => import('./pages/roles/List'));
const CreateRoles = lazy(() => import('./pages/roles/Create'));
const CreateEditRole = lazy(() => import('./pages/roles/Edit'));
const Profile = lazy(() => import('./pages/Profile'));
const Integration = lazy(() => import('./pages/integration/Add'));
const Setting = lazy(() => import('./pages/Setting'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Page404 = lazy(() => import('./pages/Page404'));

const LoanRequestList = lazy(() => import('./pages/loan-request/List'));
const CreateLoanRequest = lazy(() => import('./pages/loan-request/Create'));
const EditLoanRequest = lazy(() => import('./pages/loan-request/Edit'));
const ViewLender = lazy(() => import('./pages/loan-request/View'));
const ViewLenderDetail = lazy(() => import('./pages/loan-request/ViewDetail'));
const ViewBorrowerDetail = lazy(() => import('./pages/loan-request/ViewBorrower'));
const ViewBrokerDetail = lazy(() => import('./pages/loan-request/ViewBroker'));

const BorrowerList = lazy(() => import('./pages/borrower/List'));
const CreateBorrower  = lazy(() => import('./pages/borrower/Create'));
const EditBorrower  = lazy(() => import('./pages/borrower/Edit'));

const BrokerList = lazy(()=>import('./pages/broker/List'))
const CreateBroker = lazy(()=> import('./pages/broker/Create'))
const EditBroker = lazy(() => import('./pages/broker/Edit'));

const LenderList = lazy(() => import('./pages/lender/List'));
const CreateLender = lazy(() => import('./pages/lender/Create'));
const EditLender  = lazy(() => import('./pages/lender/Edit'));

const PaymentList = lazy(() => import('./pages/payment/List'));
const PaymentViewDetails = lazy(() => import('./pages/payment/View'));

const BlogsList = lazy(() => import('./pages/blogs/List'));
const CreateBlogs = lazy(() => import('./pages/blogs/Create'));
const EditBlogs  = lazy(() => import('./pages/blogs/Edit'));

const StatesList = lazy(() => import('./pages/states/List'));
const CreateStates = lazy(() => import('./pages/states/Create'));
const EditStates  = lazy(() => import('./pages/states/Edit'));
const ViewLenderList = lazy(() => import('./pages/states/View'));

const NewsLetterList = lazy(() => import('./pages/newsletter/List'));
const CreateNewsLetter = lazy(() => import('./pages/newsletter/Create'));
const EditNewsLetter  = lazy(() => import('./pages/newsletter/Edit'));
const ViewNewsLetterList = lazy(() => import('./pages/newsletter/View'));

const Alert = lazy(() => import('./pages/alert/List'));
const AlertViewLender = lazy(() => import('./pages/alert/View'));
const AlertLoanRequestDetail = lazy(() => import('./pages/alert/ViewDetail'));
const AlertBorrowerDetail = lazy(() => import('./pages/alert/ViewBorrower'));


export default function Router() {
  
    const isAuthenticated = !!localStorage.getItem('token');

    const routes = useRoutes([
        {
            path: '/dashboard',
            element: <DashboardLayout />,
            children: [
                { element: <Navigate to="/dashboard/app" />, index: true },
                {
                    path: 'app',
                    // element: (
                    //     <PrivateRoute
                    //         isAuthenticated={isAuthenticated}
                    //         element={
                    //             checkPermission('View-Dashboard') ? <DashboardAppPage /> : <Navigate to="/403" />
                    //         }
                    //     />
                    // ),
                      element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={
                                <DashboardAppPage />
                            }
                        />
                    ),
                },
                {
                    path: 'users',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<UserPage />}
                        />
                    ),
                },
                {
                    path: 'users/create',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<CreateUser />}
                        />
                    ),
                },
                {
                    path: 'users/edit/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<EditUser />}
                        />
                    ),
                },
                {
                    path: 'roles',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<RoleList /> }
                        />
                    ),
                },
                {
                    path: 'roles/create',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<CreateRoles /> }
                        />
                    ),
                },
                {
                    path: 'roles/edit/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<CreateEditRole />}
                        />
                    ),
                },
                // Loan Request
                {
                    path: 'loan-request',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<LoanRequestList /> }
                        />
                    ),
                },
                {
                    path: 'loan-request/create',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={ <CreateLoanRequest/> }
                        />
                    ),
                },
                {
                    path: 'loan-request/edit/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<EditLoanRequest />}
                        />
                    ),
                },
                {
                    path: 'loan-request/view/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<ViewLender/>}
                        />
                    ),
                },
                {
                    path: 'loan-request/view-details/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<ViewLenderDetail/>}
                        />
                    ),
                },
                {
                    path: 'loan-request/view-borrower/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<ViewBorrowerDetail/>}
                        />
                    ),
                },
                {
                    path: 'loan-request/view-broker/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<ViewBrokerDetail/>}
                        />
                    ),
                },

                // Borrower
                {
                    path: 'borrower',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<BorrowerList /> }
                        />
                    ),
                },
                {
                    path: 'borrower/create',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={ <CreateBorrower/> }
                        />
                    ),
                },
                {
                    path: 'borrower/edit/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<EditBorrower/>}
                        />
                    ),
                },
              
                //  {
                //     path: 'borrower-management/view/:id',
                //     element: (
                //         <PrivateRoute
                //             isAuthenticated={isAuthenticated}
                //             element={<ViewLoanRequest /> }
                //         />
                //     ),
                // }, 

                 // Broker
                {
                    path: 'broker',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<BrokerList /> }
                        />
                    ),
                },
                {
                    path: 'broker/create',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={ <CreateBroker/> }
                        />
                    ),
                },
                {
                    path: 'broker/edit/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<EditBroker/>}
                        />
                    ),
                },



                // lender
                {
                    path: 'lenders',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<LenderList /> } 
                        />
                    ),
                },
                {
                    path: 'lenders/create',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={ <CreateLender/> }
                        />
                    ),
                },
                {
                    path: 'lenders/edit/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<EditLender/>}
                        />
                    ),
                },

                // Payment
                {
                    path: 'payment',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<PaymentList /> }
                        />
                    ),
                },
                {
                    path: 'payment/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<PaymentViewDetails/>}
                        />
                    ),
                },
               
                // States
                {
                    path: 'states',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<StatesList /> }
                        />
                    ),
                },
                {
                    path: 'states/create',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={ <CreateStates/> }
                        />
                    ),
                },
                {
                    path: 'states/edit/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<EditStates/>}
                        />
                    ),
                },
                {
                    path: 'states/view/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<ViewLenderList/>}
                        />
                    ),
                },
    
    // NewsLetter
                {
                    path: 'newsletter',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<NewsLetterList /> }
                        />
                    ),
                },
                {
                    path: 'newsletter/create',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={ <CreateNewsLetter/> }
                        />
                    ),
                },
                {
                    path: 'newsletter/edit/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<EditNewsLetter/>}
                        />
                    ),
                },
                {
                    path: 'newsletter/view/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<ViewNewsLetterList/>}
                        />
                    ),
                },

                    // Alert
                {
                    path: 'alert',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<Alert /> }
                        />
                    ),
                },
                {
                    path: 'alert/view-details/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<AlertLoanRequestDetail/>}
                        />
                    ),
                },
                {
                    path: 'alert/view-borrower/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<AlertBorrowerDetail/>}
                        />
                    ),
                },
                {
                    path: 'alert/view/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<AlertViewLender/>}
                        />
                    ),
                },

                
                // Blogs
                {
                    path: 'blogs',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<BlogsList /> }
                        />
                    ),
                },
                {
                    path: 'blogs/create',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={ <CreateBlogs/> }
                        />
                    ),
                },
                {
                    path: 'blogs/edit/:id',
                    element: (
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            element={<EditBlogs/>}
                        />
                    ),
                },

                // profile
                // {
                //     path: 'profile',
                //     element: (
                //         <PrivateRoute
                //             isAuthenticated={isAuthenticated}
                //             element={<Profile /> }
                //         />
                //     ),
                // },
                   // Log history        
                // {
                //     path: 'logs',
                //     element: (
                //         <PrivateRoute
                //             isAuthenticated={isAuthenticated}
                //             element={checkPermission('View-Notification') ? <LogHistory /> : <Navigate to="/403" />}
                //         />
                //     ),
                // },
             
                // {
                //     path: 'logs/:id',
                //     element: (
                //         <PrivateRoute
                //             isAuthenticated={isAuthenticated}
                //             element={checkPermission('View-Notification') ? <ViewLog /> : <Navigate to="/403" />}
                //         />
                //     ),
                // },

                // integration 
                // {
                //     path: 'integration',
                //     element: (
                //         <PrivateRoute
                //             isAuthenticated={isAuthenticated}
                //             element={<Integration />}
                //         />
                //     ),
                // },

                  // setting 
                // {
                //     path: 'setting',
                //     element: (
                //         <PrivateRoute
                //             isAuthenticated={isAuthenticated}
                //             element={ <Setting />}
                //         />
                //     ),
                // },
            ],
        },
        {
            path: 'login',
            element: <LoginPage />,
        },
        {
            element: <SimpleLayout />,
            children: [
                { element: <Navigate to="/login" />, index: true },
                {
                    path: '404',
                    element: <Page404 />,
                },
                { path: '*', element: <Navigate to="/404" /> },
            ],
        },
        {
            path: '*',
            element: <Navigate to="/404" replace />,
        },
    ]);

    return routes;
}
